<template>
  <div class="sales-information-form">
    <div class="card-header">
      <h5 class="card-title">
        <i class="fas fa-dollar-sign text-indigo-600 mr-2" />
        Sales Information
      </h5>
    </div>

    <div
      v-if="loading"
      class="card-body">
      <div class="flex items-center justify-center py-8">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-3xl text-indigo-600 mb-2" />
          <p class="text-sm text-gray-500">
            Loading sales info...
          </p>
        </div>
      </div>
    </div>

    <div
      v-else
      class="card-body">
      <!-- View Mode -->
      <div v-if="!isEditing">
        <div
          v-if="hasSalesInfo && salesInfo"
          class="space-y-4">
          <!-- Main Info Grid -->
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">
                <i class="fas fa-user-tie mr-1.5" />
                Sales Person
              </div>
              <div class="info-value font-medium">
                {{ formatSalesPerson(salesInfo.salesPerson) }}
              </div>
            </div>

            <div
              v-if="salesInfo.bdr"
              class="info-item">
              <div class="info-label">
                <i class="fas fa-user mr-1.5" />
                BDR
              </div>
              <div class="info-value">
                {{ salesInfo.bdr }}
              </div>
            </div>

            <div class="info-item">
              <div class="info-label">
                <i class="fas fa-calendar-check mr-1.5" />
                Date Closed
              </div>
              <div class="info-value">
                <i class="fas fa-clock text-gray-400 mr-1" />
                {{ formatDate(salesInfo.dateClosed) }}
              </div>
            </div>

            <div
              v-if="salesInfo.quote"
              class="info-item">
              <div class="info-label">
                <i class="fas fa-dollar-sign mr-1.5" />
                Quote
              </div>
              <div class="info-value font-semibold text-green-600">
                {{ salesInfo.quote }}
              </div>
            </div>
          </div>

          <!-- Call Recording Section -->
          <div
            v-if="salesInfo.callRecordingUrl"
            class="recording-section">
            <div class="recording-header">
              <i class="fas fa-phone-volume mr-1.5" />
              Call Recording
            </div>
            <div class="recording-body">
              <a
                :href="salesInfo.callRecordingUrl"
                target="_blank"
                class="recording-link">
                <i class="fas fa-play-circle mr-2" />
                Listen to Sales Call Recording
                <i class="fas fa-external-link-alt ml-2 text-xs" />
              </a>
            </div>
          </div>

          <!-- Notes Section -->
          <div
            v-if="salesInfo.notes"
            class="notes-section">
            <div class="notes-header">
              <i class="fas fa-sticky-note mr-1.5" />
              Sales Notes
            </div>
            <div class="notes-body">
              {{ salesInfo.notes }}
            </div>
          </div>
        </div>
        <div
          v-else
          class="empty-state">
          <i class="fas fa-handshake empty-state-icon" />
          <p class="empty-state-text">
            No sales information available
          </p>
        </div>

        <div class="mt-4">
          <button
            class="action-button action-button-primary"
            @click="startEditing">
            <i class="fas fa-edit mr-1.5" />
            {{ hasSalesInfo ? 'Edit Sales Information' : 'Add Sales Information' }}
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <form
        v-else
        class="space-y-4"
        @submit.prevent="saveSalesInfo">
        <div class="form-group">
          <label class="form-label">Sales Person</label>
          <select
            v-model="form.sales_person"
            class="form-input">
            <option value="">
              Select sales person
            </option>
            <option value="bdr1">
              BDR 1
            </option>
            <option value="bdr2">
              BDR 2
            </option>
            <option value="account_executive1">
              Account Executive 1
            </option>
            <option value="account_executive2">
              Account Executive 2
            </option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">BDR (Optional)</label>
          <input
            v-model="form.bdr"
            type="text"
            class="form-input"
            placeholder="e.g., John Smith">
        </div>

        <div class="form-group">
          <label class="form-label">Date Closed</label>
          <input
            v-model="form.date_closed"
            type="date"
            class="form-input">
        </div>

        <div class="form-group">
          <label class="form-label">Quote</label>
          <input
            v-model="form.quote"
            type="text"
            class="form-input"
            placeholder="e.g., $500/month">
          <p class="form-helper-text">
            Include plan type (GPP, Bundle, SiteSense, etc.) and pricing details
          </p>
        </div>

        <div class="form-group">
          <label class="form-label">Call Recording URL</label>
          <input
            v-model="form.call_recording_url"
            type="url"
            class="form-input"
            :class="{ 'form-input-error': errors.call_recording_url }"
            placeholder="https://example.com/recording">
          <p
            v-if="errors.call_recording_url"
            class="form-error">
            {{ errors.call_recording_url }}
          </p>
        </div>

        <div class="form-group">
          <label class="form-label">Notes</label>
          <textarea
            v-model="form.notes"
            rows="6"
            class="form-input"
            placeholder="Capture key details from the sales call..." />
          <p class="form-helper-text">
            Please include as much detail as possible: customer needs, pain points, objections,
            decision criteria, timeline, budget constraints, competitors mentioned, and any special
            requirements or concerns discussed during the call.
          </p>
        </div>

        <div class="form-actions">
          <div class="flex gap-2">
            <button
              type="submit"
              :disabled="saving"
              class="action-button action-button-success">
              <i
                v-if="saving"
                class="fas fa-spinner fa-spin mr-1.5" />
              <i
                v-else
                class="fas fa-save mr-1.5" />
              Save
            </button>
            <button
              type="button"
              :disabled="saving"
              class="action-button action-button-secondary"
              @click="cancelEditing">
              Cancel
            </button>
          </div>
          <button
            v-if="hasSalesInfo"
            type="button"
            :disabled="saving"
            class="action-button action-button-danger"
            @click="deleteSalesInfo">
            <i
              v-if="deleting"
              class="fas fa-spinner fa-spin mr-1.5" />
            <i
              v-else
              class="fas fa-trash mr-1.5" />
            Delete
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// Store organization created date
const organizationCreatedAt = ref(null)

// State
const loading = ref(true)
const saving = ref(false)
const deleting = ref(false)
const isEditing = ref(false)
const salesInfo = ref(null)
const form = ref({
  sales_person: '',
  bdr: '',
  date_closed: '',
  quote: '',
  call_recording_url: '',
  notes: ''
})
const errors = ref({})

// Computed
const hasSalesInfo = computed(() => salesInfo.value && (
  salesInfo.value.salesPerson ||
    salesInfo.value.bdr ||
    salesInfo.value.dateClosed ||
    salesInfo.value.quote ||
    salesInfo.value.callRecordingUrl ||
    salesInfo.value.notes
))

// Methods
const fetchSalesInfo = async() => {
  try {
    loading.value = true

    // Fetch organization data to get created_at
    const orgResponse = await axios.get(`/ajax/organizations/${props.organizationId}`)
    if (orgResponse.data.createdAt) {
      organizationCreatedAt.value = orgResponse.data.createdAt.split('T')[0]
    }

    // Fetch sales information
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/sales_information`)
    if (response.data.success) {
      salesInfo.value = response.data.salesInformation
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching sales info:', error)
    mdtoast.error('Failed to load sales information', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const formatSalesPerson = (salesPerson) => {
  const names = {
    bdr1: 'BDR 1',
    bdr2: 'BDR 2',
    account_executive1: 'Account Executive 1',
    account_executive2: 'Account Executive 2'
  }
  return names[salesPerson] || 'Not assigned'
}

const formatDate = (dateString) => {
  if (!dateString) return 'Not set'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const startEditing = () => {
  if (salesInfo.value) {
    form.value = {
      sales_person: salesInfo.value.salesPerson || '',
      bdr: salesInfo.value.bdr || '',
      date_closed: salesInfo.value.dateClosed ? salesInfo.value.dateClosed.split('T')[0] : organizationCreatedAt.value || '',
      quote: salesInfo.value.quote || '',
      call_recording_url: salesInfo.value.callRecordingUrl || '',
      notes: salesInfo.value.notes || ''
    }
  } else {
    form.value = {
      sales_person: '',
      bdr: '',
      date_closed: organizationCreatedAt.value || '',
      quote: '',
      call_recording_url: '',
      notes: ''
    }
  }
  errors.value = {}
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
  form.value = {
    sales_person: '',
    bdr: '',
    date_closed: '',
    quote: '',
    call_recording_url: '',
    notes: ''
  }
  errors.value = {}
}

const validateForm = () => {
  errors.value = {}
  let isValid = true

  if (form.value.call_recording_url) {
    try {
      new URL(form.value.call_recording_url)
    } catch {
      errors.value.call_recording_url = 'Invalid URL format'
      isValid = false
    }
  }

  return isValid
}

const saveSalesInfo = async() => {
  if (!validateForm()) return

  try {
    saving.value = true
    const payload = {
      sales_information: {
        sales_person: form.value.sales_person,
        bdr: form.value.bdr,
        call_recording_url: form.value.call_recording_url,
        quote: form.value.quote,
        date_closed: form.value.date_closed,
        notes: form.value.notes
      }
    }
    // console.log('Sending payload:', payload)
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/sales_information`,
      payload
    )

    if (response.data.success) {
      salesInfo.value = response.data.salesInformation
      isEditing.value = false
      mdtoast.success('Sales information saved successfully', { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || 'Failed to save sales information', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving sales info:', error)
    mdtoast.error('Failed to save sales information', { duration: 3000 })
  } finally {
    saving.value = false
  }
}

const deleteSalesInfo = async() => {
  // eslint-disable-next-line no-alert
  if (!window.confirm('Are you sure you want to delete the sales information?')) return

  try {
    deleting.value = true
    const response = await axios.delete(`/ajax/organizations/${props.organizationId}/sales_information`)

    if (response.data.success) {
      salesInfo.value = null
      isEditing.value = false
      mdtoast.success('Sales information deleted successfully', { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || 'Failed to delete sales information', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error deleting sales info:', error)
    mdtoast.error('Failed to delete sales information', { duration: 3000 })
  } finally {
    deleting.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchSalesInfo()
})
</script>

<style scoped>
.sales-information-form {
  @apply bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden;
}

.card-header {
  @apply px-5 py-4 bg-gray-50 border-b border-gray-200;
}

.card-title {
  @apply text-base font-semibold text-gray-900 flex items-center;
}

.card-body {
  @apply p-5;
}

.info-grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.info-item {
  @apply space-y-1;
}

.info-label {
  @apply text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center;
}

.info-value {
  @apply text-sm text-gray-900;
}

.recording-section {
  @apply bg-indigo-50 rounded-lg overflow-hidden border border-indigo-100;
}

.recording-header {
  @apply px-4 py-2 bg-indigo-100 border-b border-indigo-200 text-sm font-medium text-indigo-700
         flex items-center;
}

.recording-body {
  @apply p-4;
}

.recording-link {
  @apply text-sm text-indigo-600 hover:text-indigo-800 transition-colors
         inline-flex items-center hover:underline font-medium;
}

.notes-section {
  @apply bg-gray-50 rounded-lg overflow-hidden border border-gray-200;
}

.notes-header {
  @apply px-4 py-2 bg-gray-100 border-b border-gray-200 text-sm font-medium text-gray-700
         flex items-center;
}

.notes-body {
  @apply p-4 text-sm text-gray-700 whitespace-pre-wrap;
}

.empty-state {
  @apply text-center py-8;
}

.empty-state-icon {
  @apply text-4xl text-gray-300 mb-3;
}

.empty-state-text {
  @apply text-sm text-gray-500;
}

.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-medium text-gray-700;
}

.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
         transition-colors duration-200;
}

.form-input-error {
  @apply border-red-500 focus:ring-red-500 focus:border-red-500;
}

.form-error {
  @apply mt-1 text-xs text-red-600;
}

.form-helper-text {
  @apply mt-1 text-xs text-gray-500 break-words;
}

.form-actions {
  @apply flex items-center justify-between mt-6 pt-4 border-t border-gray-200;
}

.action-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.action-button-primary {
  @apply bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500;
}

.action-button-success {
  @apply bg-green-600 text-white hover:bg-green-700 focus:ring-green-500;
}

.action-button-secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500;
}

.action-button-danger {
  @apply bg-red-600 text-white hover:bg-red-700 focus:ring-red-500;
}
</style>
